import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
	Tabs,
	Tab,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import { OnoffType } from '@/api/enum/common.enum';
import { showLabel } from '@/utils/helper';
import MeditationListSummaryFilterSection from './components/AchievementSummaryFilterSection';
import SetMeditationOrderDialog from './components/SetAchievementOrderDialog';
import { useGetAchievementListQuery } from '@/api/task/achievementApiSlice';
import {
	AchievementFilter,
	TaskAchievementCumulativeSeriesDTO,
} from '@/api/DTO/task/achievement.interface';
import { PUBLISHED_LISTB } from '@/common/constants/common';
import PublishedChip from '@/common/components/PublishedChip';

export interface DataGrid {
	updatedAt?: string;
	createdAt?: string;
	publishedStartAt?: string;
	publishedEndAt?: string;
	id?: string;
	published?: number | string | boolean;
}

export default function AchievementSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useState('achievementTask');

	const defaultPropertyFilter: AchievementFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		publishedList: [],
	};
	const defaultValues: DefaultValues<AchievementFilter> = defaultPropertyFilter;
	const methods = useForm<AchievementFilter>({
		defaultValues,
	});
	const transformedSearchParams: AchievementFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'publishedList',
		]),
	};
	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetAchievementListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback(
		(response?: TaskAchievementCumulativeSeriesDTO[]) => {
			if (response == null) return;
			const newResponse: DataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					createdAt: !item.createdAt
						? '-'
						: dayjs(item.createdAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					updatedAt: !item.updatedAt
						? '-'
						: dayjs(item.updatedAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					publishedStartAt: !item.publishedStartAt
						? '-'
						: dayjs(item.publishedStartAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					publishedEndAt: !item.publishedEndAt
						? '-'
						: dayjs(item.publishedEndAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});
			return newResponse;
		},
		[]
	);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as AchievementFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const columns: GridColDef[] = [
		{
			field: 'titleZh',
			headerName: t('achievementList.seriesName'),
			flex: 20,
		},
		{
			field: 'finishedConditionName',
			headerName: t('achievementList.completionConditionsApp'),
			flex: 20,
		},
		{
			field: 'publishedSingleNum',
			headerName: t('achievementList.taskTotal'),
			flex: 20,
		},
		{
			field: 'published',
			headerName: t('achievementList.seriesStatus'),
			flex: 20,
			renderCell: (params: any) => (
				<PublishedChip
					published={params.row.published}
					publishList={PUBLISHED_LISTB}
				/>
			),
		},
		{
			field: 'publishedStartAt',
			headerName: t('achievementList.pushedTime'),
			flex: 10,
		},
		{
			field: 'publishedEndAt',
			headerName: t('achievementList.pulledTime'),
			flex: 10,
		},
		{
			field: 'updatedAt',
			headerName: t('achievementList.updateTime'),
			flex: 10,
		},
		{
			field: 'createdAt',
			headerName: t('achievementList.createTime'),
			flex: 10,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link
					to={`/task-manage/achievement-list/info/${params.id}`}
					state={{
						propertySummarySearchParams: qs.stringify(transformedSearchParams),
					}}
				>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
		navigate(`/task-manage/achievement-list/treasure`);
	};

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{t('drawerListItems.achievementList')}
				</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					<SetMeditationOrderDialog />
					<Button
						variant='contained'
						style={{ alignItems: 'center' }}
						endIcon={<NoteAltOutlinedIcon />}
						onClick={() => navigate(`/task-manage/achievement-list/add`)}
					>
						{t('achievementList.createNewSeries')}
					</Button>
				</Stack>
			</Box>

			<Tabs value={currentTab} onChange={handleTabChange} sx={{ marginTop: 3 }}>
				<Tab
					label={
						<Typography variant='body1'>
							{t('drawerListItems.achievementTask')}
						</Typography>
					}
					value='achievementTask'
				/>
				<Tab
					label={
						<Typography variant='body1'>
							{t('drawerListItems.treasurTask')}
						</Typography>
					}
					value='treasurTask'
				/>
			</Tabs>

			<Card sx={{ marginTop: 1 }}>
				<CardContent>
					<FormProvider {...methods}>
						<MeditationListSummaryFilterSection></MeditationListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
